import type { FundDetail } from "./fund-detail-types";

export type OutcomePeriodState = "pre-launch" | "in-period" | "post-period";

export interface BufferMathInput {
  outcomePeriodStart: Date;
  outcomePeriodEnd: Date;
  bufferLevel: number;
  bufferStart: number;
  startingCapGrossPct: number;
  startingCapNetPct: number;
  refPriceAtPeriodStart?: number;
  fundNavAtPeriodStart?: number;
  refPriceNow?: number;
  fundNavNow?: number;
  now?: Date;
}

export interface BufferMathOutput {
  state: OutcomePeriodState;
  progress: number;
  remainingDays: number;
  refReturnPct: number | null;
  fundReturnPct: number | null;
  remainingCapPct: number | null;
  remainingBufferPct: number | null;
  downsideBeforeBufferPct: number | null;
  participation: number | null;
  projectedFundReturnAtCurrentRefPct: number | null;
}

export function fundReturnAtRef(
  refReturnPct: number,
  capPct: number,
  bufferLevel: number,
  bufferStart: number,
): number {
  if (refReturnPct >= 0) return Math.min(refReturnPct, capPct);
  const drop = -refReturnPct;
  if (bufferStart === 0) {
    if (drop <= bufferLevel) return 0;
    return refReturnPct + bufferLevel;
  }
  const bufferEnd = bufferStart + bufferLevel;
  if (drop <= bufferStart) return refReturnPct;
  if (drop <= bufferEnd) return -bufferStart;
  return refReturnPct + bufferLevel;
}

export function computeBufferState(input: BufferMathInput): BufferMathOutput {
  const { outcomePeriodStart, outcomePeriodEnd, bufferLevel, bufferStart, startingCapNetPct } =
    input;
  const now = input.now ?? new Date();
  const dayMs = 86_400_000;
  const totalMs = outcomePeriodEnd.getTime() - outcomePeriodStart.getTime();
  const elapsedMs = now.getTime() - outcomePeriodStart.getTime();

  let state: OutcomePeriodState;
  let progress: number;
  let remainingDays: number;

  if (now < outcomePeriodStart) {
    state = "pre-launch";
    progress = 0;
    remainingDays = Math.ceil((outcomePeriodStart.getTime() - now.getTime()) / dayMs);
  } else if (now >= outcomePeriodEnd) {
    state = "post-period";
    progress = 1;
    remainingDays = 0;
  } else {
    state = "in-period";
    progress = Math.min(1, Math.max(0, elapsedMs / totalMs));
    remainingDays = Math.ceil((outcomePeriodEnd.getTime() - now.getTime()) / dayMs);
  }

  if (state !== "in-period") {
    return {
      state,
      progress,
      remainingDays,
      refReturnPct: null,
      fundReturnPct: null,
      remainingCapPct: null,
      remainingBufferPct: null,
      downsideBeforeBufferPct: null,
      participation: null,
      projectedFundReturnAtCurrentRefPct: null,
    };
  }

  const refReturnPct =
    input.refPriceNow != null &&
    input.refPriceAtPeriodStart != null &&
    input.refPriceAtPeriodStart > 0
      ? (input.refPriceNow / input.refPriceAtPeriodStart - 1) * 100
      : null;

  const fundReturnPct =
    input.fundNavNow != null && input.fundNavAtPeriodStart != null && input.fundNavAtPeriodStart > 0
      ? (input.fundNavNow / input.fundNavAtPeriodStart - 1) * 100
      : null;

  const remainingCapPct =
    fundReturnPct != null && startingCapNetPct > 0
      ? ((1 + startingCapNetPct / 100) / (1 + fundReturnPct / 100) - 1) * 100
      : null;

  let remainingBufferPct: number | null = null;
  let downsideBeforeBufferPct: number | null = null;
  if (refReturnPct != null) {
    const drop = Math.max(0, -refReturnPct);
    if (drop <= bufferStart) {
      remainingBufferPct = bufferLevel;
      downsideBeforeBufferPct = bufferStart - drop;
    } else {
      const used = Math.min(drop - bufferStart, bufferLevel);
      remainingBufferPct = bufferLevel - used;
      downsideBeforeBufferPct = 0;
    }
  }

  const participation =
    refReturnPct != null && refReturnPct !== 0 && fundReturnPct != null
      ? fundReturnPct / refReturnPct
      : null;

  const projectedFundReturnAtCurrentRefPct =
    refReturnPct != null && startingCapNetPct > 0
      ? fundReturnAtRef(refReturnPct, startingCapNetPct, bufferLevel, bufferStart)
      : null;

  return {
    state,
    progress,
    remainingDays,
    refReturnPct,
    fundReturnPct,
    remainingCapPct,
    remainingBufferPct,
    downsideBeforeBufferPct,
    participation,
    projectedFundReturnAtCurrentRefPct,
  };
}

export function bufferMathFromFund(
  fund: FundDetail,
  live: {
    refPriceNow?: number;
    fundNavNow?: number;
    fundNavAtPeriodStart?: number;
    refPriceAtPeriodStart?: number;
  } = {},
  now?: Date,
): BufferMathOutput | null {
  if (!fund.outcomePeriodStart || !fund.outcomePeriodEnd || fund.bufferLevel == null) {
    return null;
  }
  return computeBufferState({
    outcomePeriodStart: new Date(fund.outcomePeriodStart),
    outcomePeriodEnd: new Date(fund.outcomePeriodEnd),
    bufferLevel: fund.bufferLevel,
    bufferStart: fund.bufferStart ?? 0,
    startingCapGrossPct:
      fund.startingCapGrossPct ?? parseCapPct(fund.bufferOutcomeDetails, "gross") ?? 0,
    startingCapNetPct: fund.startingCapNetPct ?? parseCapPct(fund.bufferOutcomeDetails, "net") ?? 0,
    refPriceAtPeriodStart: live.refPriceAtPeriodStart ?? fund.refPriceAtPeriodStart,
    fundNavAtPeriodStart: live.fundNavAtPeriodStart ?? fund.fundNavAtPeriodStart,
    refPriceNow: live.refPriceNow,
    fundNavNow: live.fundNavNow,
    now,
  });
}

function parseCapPct(
  details: { label: string; value: string }[] | undefined,
  variant: "gross" | "net",
): number | undefined {
  if (!details) return undefined;
  const row = details.find(
    (d) => d.label.toLowerCase().includes("cap") && d.label.toLowerCase().includes(variant),
  );
  if (!row) return undefined;
  const num = Number.parseFloat(row.value.replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(num) ? num : undefined;
}

export function formatPct(v: number | null, opts: { signed?: boolean } = {}): string {
  if (v == null || !Number.isFinite(v)) return "TBD";
  const sign = opts.signed && v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}${Math.abs(v).toFixed(2)}%`;
}
